/**
 * **The day's incident log** (Watchtower, the ninth overnight): every incident opened on the
 * operating day, in order — when, what, where, who went, how it closed and the report. Printed at
 * close, kept for the insurer. A printout carries its print time; it is a photograph, not a view.
 */
import type { ReactNode } from 'react';
import { MARK_COLOR, PrintRoot, formatDateTime, formatTime } from '@cider/ui';
import { useLoad } from '../views/admin/shared.js';

interface Row {
  id: string;
  kindWords: string;
  what: string;
  place: string;
  going: string | null;
  state: string;
  openedAt: string;
  openedBy: string | null;
  closedAt: string | null;
  closedBy: string | null;
  report: string | null;
}

export function IncidentLog({ year }: { year: string }): ReactNode {
  const list = useLoad<{ rows: Row[]; says: string }>(`/ops/incidents?year=${year}&which=today`);
  if (list.error) return <p role="alert">{list.error}</p>;
  const d = list.data;
  if (!d) return <p>Reading the log…</p>;
  return (
    <PrintRoot
      kind="letter"
      title={`The incident log — ${year}`}
      pages={[
        <div key="1" className="letter sheet">
          <div className="sheet__head">
            <img className="report__mark" src={MARK_COLOR} alt="Apple Fest" />
            <div className="sheet__eyebrow">SAINT LAWRENCE APPLE FEST · WATCHTOWER</div>
            <h1>The incident log</h1>
            <div className="sheet__when">
              {d.says} · printed {formatDateTime(new Date())}
            </div>
          </div>
          <table className="sheet__table">
            <thead>
              <tr>
                <th>Opened</th>
                <th>What</th>
                <th>Where</th>
                <th>Who went</th>
                <th>Closed · the report</th>
              </tr>
            </thead>
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {formatTime(r.openedAt)}
                    {r.openedBy ? ` · ${r.openedBy}` : ''}
                  </td>
                  <td className="sheet__name">
                    {r.kindWords} · {r.what}
                  </td>
                  <td>{r.place}</td>
                  <td>{r.going ?? ''}</td>
                  <td>
                    {r.state === 'closed'
                      ? `${formatTime(r.closedAt ?? r.openedAt)}${r.closedBy ? ` · ${r.closedBy}` : ''} · ${r.report ?? ''}`
                      : 'STILL OPEN'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {d.rows.length === 0 ? <p className="sheet__note">Nothing was opened today.</p> : null}
        </div>,
      ]}
    />
  );
}
